import React, { useState, useEffect } from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { fetchLSTMAnalysis } from '../services/api';

const LSTMDefectAnalysis = ({ selectedShotNumber, shotHistoryData = [], equipmentId, craftId }) => {
  const [lstmData, setLstmData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lstmShotHistory, setLstmShotHistory] = useState([]); // LSTM용 샷 히스토리 (선택 샷 기준 이전 50개)

  // LSTM 분석 데이터 및 샷 히스토리 로드
  useEffect(() => {
    const loadLSTMData = async () => {
      if (!equipmentId) {
        setError('설비 ID가 없습니다.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Mock API 사용
        const result = await fetchLSTMAnalysis(equipmentId, craftId, selectedShotNumber);

        if (result.success && result.data) {
          setLstmData(result.data);
        }

        // shotHistoryData를 lstmShotHistory로 사용
        if (shotHistoryData && shotHistoryData.length > 0) {
          setLstmShotHistory(shotHistoryData.slice(0, 50));
        }

        setError(null);
      } catch (err) {
        console.error('LSTM 데이터 로드 실패:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadLSTMData();
  }, [equipmentId, craftId, selectedShotNumber, shotHistoryData]);

  // Attention Weight 차트 데이터 생성
  const attentionChartData = lstmData?.attention_weights?.map((aw, idx) => ({
    shot: (aw.label || `Shot ${aw.shot_index || idx + 1}`).replace('Shot ', ''),  // "Shot " 제거, 숫자만 표시
    attention: aw.weight || 0,
    label: aw.label || ''
  })) || [];

  // 주요 파라미터 변화 추이 데이터 생성 (선택된 샷 기준으로 이전 50샷, 0~100 정규화)
  const timeSeriesData = React.useMemo(() => {
    // lstmShotHistory 우선 사용, 없으면 shotHistoryData에서 선택된 샷 기준으로 필터링
    let dataSource = [];

    if (lstmShotHistory.length > 0) {
      dataSource = lstmShotHistory;
    } else if (shotHistoryData.length > 0) {
      // shotHistoryData에서 선택된 샷 기준으로 필터링
      if (selectedShotNumber) {
        // shotHistoryData는 DESC 정렬 (최신이 앞)
        // 선택된 샷의 인덱스를 찾아서 그 위치부터 50개 추출
        const selectedIdx = shotHistoryData.findIndex(s => s.shotCount === selectedShotNumber);
        if (selectedIdx !== -1) {
          // 선택된 샷부터 이전 50개 (DESC이므로 인덱스 증가 방향이 과거)
          dataSource = shotHistoryData.slice(selectedIdx, selectedIdx + 50);
        } else {
          dataSource = shotHistoryData.slice(0, 50);
        }
      } else {
        dataSource = shotHistoryData.slice(0, 50);
      }
    }

    if (!dataSource || dataSource.length === 0) return [];

    // reverse하여 오래된 샷이 왼쪽, 선택된 샷이 오른쪽에 오도록
    const recentShots = [...dataSource].reverse();

    // 각 파라미터의 min/max 계산
    const speeds = recentShots.map(s => s.injSpeed1 || 0).filter(v => v > 0);
    const temps = recentShots.map(s => s.temp1Current || 0).filter(v => v > 0);
    const pressures = recentShots.map(s => s.injPress1 || 0).filter(v => v > 0);

    const speedMin = Math.min(...speeds);
    const speedMax = Math.max(...speeds);
    const tempMin = Math.min(...temps);
    const tempMax = Math.max(...temps);
    const pressureMin = Math.min(...pressures);
    const pressureMax = Math.max(...pressures);

    // 정규화 함수 (0~100)
    const normalize = (value, min, max) => {
      if (max === min) return 50; // 모든 값이 같으면 중간값
      return ((value - min) / (max - min)) * 100;
    };

    // 오래된 순서대로 매핑 (좌측=오래된 샷, 우측=선택된 샷)
    return recentShots.map((shot, idx) => ({
      shot: shot.shotCount || idx + 1,
      speed: shot.injSpeed1 ? normalize(shot.injSpeed1, speedMin, speedMax) : null,
      temperature: shot.temp1Current ? normalize(shot.temp1Current, tempMin, tempMax) : null,
      pressure: shot.injPress1 ? normalize(shot.injPress1, pressureMin, pressureMax) : null,
    }));
  }, [lstmShotHistory, shotHistoryData, selectedShotNumber]);

  // 주요 원인 인자 중요도 (실제 데이터 사용)
  const factorImportance = React.useMemo(() => {
    if (!lstmData?.feature_importance || lstmData.feature_importance.length === 0) {
      return [];
    }

    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#FFA07A', '#98D8C8'];

    return lstmData.feature_importance.slice(0, 5).map((item, idx) => ({
      factor: item.feature_kr || item.feature,
      importance: item.importance / 100, // 0-1 범위로 정규화
      color: colors[idx % colors.length],
      rank: item.rank
    }));
  }, [lstmData]);

  // 레이더 차트 데이터 (실제 추천값 기반, 0-10 정규화)
  const radarData = React.useMemo(() => {
    if (!lstmData?.recommendations || lstmData.recommendations.length === 0) {
      return [];
    }

    // 상위 5개 추천값 사용
    const topRecs = lstmData.recommendations.slice(0, 5);

    return topRecs.map((rec) => {
      const currentValue = rec.current_value ?? 0;
      const optimalValue = rec.recommended_value ?? 0;
      const normalMax = optimalValue * 1.05;  // 상한 (추천값 +5%)
      const normalMin = optimalValue * 0.95;  // 하한 (추천값 -5%)

      // 정상범위를 기준으로 0-10 정규화
      // normalMin=0, optimalValue=5, normalMax=10
      const normalize = (value) => {
        if (normalMax === normalMin) return 5;
        const ratio = (value - normalMin) / (normalMax - normalMin);
        return ratio * 10; // 0~10 범위로 변환 (범위 밖은 음수 또는 10 초과)
      };

      return {
        category: rec.feature_kr || rec.feature,
        current: normalize(currentValue),
        optimal: 5,  // 추천값은 항상 중간값 5
        normal: 10   // 정상범위 상한은 항상 10
      };
    });
  }, [lstmData]);

  // 추천 조정값 (lstm_recommendations 데이터 사용)
  const recommendations = React.useMemo(() => {
    if (!lstmData?.recommendations || lstmData.recommendations.length === 0) {
      return [];
    }

    return lstmData.recommendations.slice(0, 5).map((rec) => {
      const currentVal = rec.current_value != null ? rec.current_value.toFixed(2) : '0.00';
      const recommendedVal = rec.recommended_value != null ? rec.recommended_value.toFixed(2) : '0.00';
      const currentNum = rec.current_value ?? 0;
      const recommendedNum = rec.recommended_value ?? 0;
      const change = (recommendedNum - currentNum).toFixed(2);
      const changeStr = change >= 0 ? `+${change}` : change;

      // 추천값 대비 실제값 비율 계산 (0-200%)
      let gaugeValue = 100; // 기본값 100%
      if (recommendedNum !== 0) {
        const ratio = (currentNum / recommendedNum) * 100;
        gaugeValue = Math.min(Math.max(ratio, 0), 200); // 0-200 범위로 제한
      }

      // 80% 이하 또는 120% 이상이면 out_of_range
      const isOutOfRange = gaugeValue <= 80 || gaugeValue >= 120;

      return {
        parameter: rec.feature_kr || rec.feature,
        current: currentVal,
        recommended: recommendedVal,
        change: changeStr,
        gaugeValue: Math.round(gaugeValue),
        status: isOutOfRange ? 'out_of_range' : 'normal',
        direction: rec.direction,
        originalStatus: rec.status
      };
    });
  }, [lstmData]);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gridTemplateRows: '3fr 3fr 1fr',
      gap: '3px',
      height: '100%',
      backgroundColor: '#0D1117'
    }}>
      {/* 1행 1~2열: 시간 구간별 Attention Weight 분석 */}
      <div style={{ gridColumn: '1 / 3', backgroundColor: '#21252b', padding: '16px', border: '1px solid #1B1D20', display: 'flex', flexDirection: 'column' }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'white', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #3F4550', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#60A5FA' }}>show_chart</span>
          시간 구간별 Attention Weight 분석
        </h4>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9CA3AF' }}>
            로딩 중...
          </div>
        ) : error ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#EF4444', fontSize: '13px' }}>
            데이터 로드 실패: {error}
          </div>
        ) : attentionChartData.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9CA3AF' }}>
            데이터가 없습니다
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={attentionChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="shot"
                  stroke="#9CA3AF"
                  style={{ fontSize: '11px' }}
                  interval="preserveStartEnd"
                />
                <YAxis stroke="#9CA3AF" domain={['auto', 'auto']} style={{ fontSize: '11px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ fontSize: '13px' }}
                  formatter={(value) => [value.toFixed(3), 'Attention']}
                />
                <defs>
                  <linearGradient id="attentionGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="attention" stroke="#EF4444" fill="url(#attentionGradient)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#9CA3AF' }}>
              * 이상 점수: {lstmData?.anomaly_score?.toFixed(3)} | 임계값: {lstmData?.threshold?.toFixed(3)} |
              {lstmData?.is_anomaly ? <span style={{ color: '#EF4444', fontWeight: '600' }}> 이상 감지</span> : <span style={{ color: '#10B981', fontWeight: '600' }}> 정상</span>}
            </div>
          </>
        )}
      </div>

      {/* 1행 3열: 불량 원인 인자 중요도 */}
      <div style={{ backgroundColor: '#21252b', padding: '16px', border: '1px solid #1B1D20', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'white', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #3F4550' }}>
          불량 원인 인자 중요도
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', flex: 1, overflowY: 'auto' }}>
          {factorImportance.map((item, idx) => (
            <div
              key={idx}
              style={{
                padding: '8px',
                backgroundColor: '#2D3339',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#30363D'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2D3339'}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: item.color }} />
                  <span style={{ fontWeight: '600', color: '#F3F4F6', fontSize: '12px' }}>{item.factor}</span>
                </div>
                <span style={{ fontSize: '11px', color: item.trend > 0 ? '#EF4444' : '#10B981', fontWeight: '600' }}>
                  {item.trend > 0 ? '+' : ''}{item.trend}%
                </span>
              </div>
              <div style={{ width: '100%', height: '6px', backgroundColor: '#1B1D20', borderRadius: '3px', overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${item.importance * 100}%`,
                    backgroundColor: item.color,
                    transition: 'width 1s ease'
                  }}
                />
              </div>
              <div style={{ marginTop: '5px', fontSize: '11px', color: '#9CA3AF' }}>
                중요도: {(item.importance * 100).toFixed(0)}%
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 2행 1~2열: 주요 파라미터 변화 추이 */}
      <div style={{ gridColumn: '1 / 3', backgroundColor: '#21252b', padding: '16px', border: '1px solid #1B1D20', display: 'flex', flexDirection: 'column' }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'white', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #3F4550', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#60A5FA' }}>insights</span>
          주요 파라미터 변화 추이 (최근 50샷)
        </h4>
        {timeSeriesData.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9CA3AF' }}>
            데이터가 없습니다
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="shot"
                stroke="#9CA3AF"
                style={{ fontSize: '11px' }}
                interval="preserveStartEnd"
              />
              <YAxis
                stroke="#9CA3AF"
                style={{ fontSize: '11px' }}
                domain={[0, 100]}
                label={{ value: '정규화 값 (0-100)', angle: -90, position: 'insideLeft', style: { fontSize: '11px', fill: '#9CA3AF' } }}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                labelStyle={{ fontSize: '13px' }}
                formatter={(value) => value !== null ? value.toFixed(1) : 'N/A'}
              />
              <Legend
                wrapperStyle={{ fontSize: '10px' }}
                iconSize={7}
                layout="horizontal"
                align="right"
                verticalAlign="top"
              />
              <Line type="monotone" dataKey="pressure" stroke="#FF6B6B" strokeWidth={2} dot={false} name="충진압력" connectNulls />
              <Line type="monotone" dataKey="temperature" stroke="#4ECDC4" strokeWidth={2} dot={false} name="용융온도" connectNulls />
              <Line type="monotone" dataKey="speed" stroke="#45B7D1" strokeWidth={2} dot={false} name="사출속도" connectNulls />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* 2행 3열: 파라미터 상태 비교 */}
      <div style={{ backgroundColor: '#21252b', padding: '16px', border: '1px solid #1B1D20', display: 'flex', flexDirection: 'column' }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'white', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #3F4550' }}>
          파라미터 상태 비교
        </h4>
        {radarData.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9CA3AF' }}>
            데이터가 없습니다
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData}>
              <PolarGrid stroke="#30363D" />
              <PolarAngleAxis dataKey="category" stroke="#9CA3AF" style={{ fontSize: '11px' }} />
              <PolarRadiusAxis angle={90} domain={[0, 10]} tick={false} stroke="transparent" />
              <Radar
                name="정상범위(±5%)"
                dataKey="normal"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.2}
              />
              <Radar
                name="현재값"
                dataKey="current"
                stroke="#EF4444"
                fill="#EF4444"
                fillOpacity={0.3}
              />
              <Radar
                name="추천값"
                dataKey="optimal"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.1}
                strokeDasharray="5 5"
              />
              <Legend wrapperStyle={{ fontSize: '10px' }} iconSize={8} />
            </RadarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* 3행 1~2열: 공정 튜닝 추천값 */}
      <div style={{ gridColumn: '1 / 3', backgroundColor: '#21252b', padding: '16px', border: '1px solid #1B1D20', display: 'flex', flexDirection: 'column' }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'white', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #3F4550' }}>
          공정 튜닝 추천값
        </h4>
        {recommendations.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9CA3AF' }}>
            추천 데이터가 없습니다
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', flex: 1 }}>
            {recommendations.map((rec, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '8px 10px',
                  backgroundColor: rec.status === 'out_of_range' ? '#2D1F1F' : '#2D3339',
                  border: rec.status === 'out_of_range' ? '1px solid #EF4444' : '1px solid transparent',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = rec.status === 'out_of_range' ? '#3D2525' : '#30363D'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = rec.status === 'out_of_range' ? '#2D1F1F' : '#2D3339'}
              >
                <div style={{ fontWeight: '600', color: '#F3F4F6', marginBottom: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {rec.parameter}
                  {rec.status === 'out_of_range' && <span style={{ fontSize: '10px', color: '#EF4444' }}>⚠</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <div style={{ fontSize: '10px', color: '#9CA3AF' }}>
                    {rec.current} → {rec.recommended}
                  </div>
                  <span style={{ color: rec.change.startsWith('+') ? '#EF4444' : rec.change.startsWith('-') ? '#10B981' : '#9CA3AF', fontWeight: '700', fontSize: '11px' }}>
                    {rec.change}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <div style={{ flex: 1, height: '5px', backgroundColor: '#1B1D20', borderRadius: '2px', overflow: 'hidden', position: 'relative' }}>
                    {/* 100% 기준선 */}
                    <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', backgroundColor: '#6B7280', zIndex: 1 }} />
                    <div
                      style={{
                        height: '100%',
                        width: `${Math.min(rec.gaugeValue / 2, 100)}%`,
                        background: rec.status === 'out_of_range' ? 'linear-gradient(to right, #EF4444, #F87171)' : 'linear-gradient(to right, #10B981, #34D399)',
                        transition: 'width 1s ease'
                      }}
                    />
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: '600', color: rec.status === 'out_of_range' ? '#EF4444' : '#10B981' }}>{rec.gaugeValue}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 3행 3열: 모델 예측 신뢰도 */}
      <div style={{ backgroundColor: '#21252b', padding: '16px', border: '1px solid #1B1D20', display: 'flex', flexDirection: 'column' }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'white', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #3F4550' }}>
          모델 예측 신뢰도
        </h4>
        {!lstmData?.confidence ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9CA3AF' }}>
            신뢰도 데이터가 없습니다
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '12px', flex: 1 }}>
            {/* 종합 신뢰도 (좌측) */}
            <div style={{ padding: '12px', backgroundColor: '#2D3339', flex: '0 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: '120px' }}>
              <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '5px' }}>종합 신뢰도</div>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#10B981' }}>
                {lstmData.confidence.overall?.toFixed(1) ?? '0.0'}%
              </div>
            </div>
            {/* 세부 지표 (우측) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: '1', justifyContent: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#9CA3AF', fontSize: '12px' }}>LSTM 예측 정확도</span>
                <span style={{ fontWeight: '700', color: '#10B981', fontSize: '13px' }}>
                  {lstmData.confidence.lstm_accuracy?.toFixed(1) ?? '0.0'}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#9CA3AF', fontSize: '12px' }}>Attention 신뢰도</span>
                <span style={{ fontWeight: '700', color: '#3B82F6', fontSize: '13px' }}>
                  {lstmData.confidence.attention_confidence?.toFixed(1) ?? '0.0'}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#9CA3AF', fontSize: '12px' }}>데이터 품질 지수</span>
                <span style={{ fontWeight: '700', color: '#F59E0B', fontSize: '13px' }}>
                  {lstmData.confidence.data_quality?.toFixed(1) ?? '0.0'}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LSTMDefectAnalysis;
